import { useQuery } from '@tanstack/react-query';
import { useProperties, useUnits } from './useProperties';
import { useBills, useReceivables } from './useAccounting';
import { useTickets } from './useMaintenance';
import { useHCVPrograms } from './useCompliance';
import { useInspections } from './useOperations';
import {
  calculateOccupancyFromUnits,
  calculateRevenue,
  calculateExpenses,
  calculateNOI,
  calculateROI,
  calculatePendingReceivables,
  calculateOverdueReceivables,
  calculateOverdueReceivableCount,
  calculatePendingBills,
  isJobCompleted,
  isJobActive
} from '@/utils/sharedMetrics';
import type { Property, Receivable, Bill, MaintenanceTicket, HCVProgram, Inspection } from '@repo/shared-types';

/**
 * Centralized PM Metrics Hook
 * 
 * This hook provides a single source of truth for all PM-derived metrics.
 * PM has access to ALL data across the entire portfolio.
 * 
 * Follows the same pattern as OwnerMetrics and InvestorMetrics for consistency.
 * 
 * Metrics are computed on-demand from properties, receivables, bills, tickets,
 * inspections, and compliance data.
 */

export interface PMMetrics {
  // Property Metrics
  totalProperties: number;
  totalUnits: number;
  occupiedUnits: number;
  occupancyRate: number;

  // Financial Metrics
  totalRevenue: number;
  collectedRevenue: number;
  totalExpenses: number;
  paidExpenses: number;
  noi: number;
  collectedNoi: number;
  roi: number;

  // Cash Flow Metrics
  pendingReceivables: number;
  overdueReceivables: number;
  /** Count of receivables in Late status (nav badge). */
  overdueReceivableCount: number;
  pendingBills: number;

  // Maintenance Metrics
  activeTickets: number;
  completedTickets: number;

  // Compliance Metrics
  totalInspections: number;
  complianceInspections: number;
  activeHCVPrograms: number;

  // Detailed Data
  properties: Property[];
  receivables: Receivable[];
  bills: Bill[];
  tickets: MaintenanceTicket[];
  inspections: Inspection[];
  hcvPrograms: HCVProgram[];
}

/**
 * Main hook for PM metrics - PM has access to all data
 */
export const usePMMetrics = () => {
  const { data: allProperties = [] } = useProperties();
  const { data: allUnits = [] } = useUnits();
  const { data: allBills = [] } = useBills();
  const { data: allReceivables = [] } = useReceivables();
  const { data: allTickets = [] } = useTickets();
  const { data: allInspections = [] } = useInspections();
  const { data: allHCVPrograms = [] } = useHCVPrograms();

  return useQuery({
    queryKey: ['pm', 'metrics'],
    queryFn: (): PMMetrics => {
      // PM sees ALL data - no filtering needed

      // 1. Property Metrics
      const totalProperties = allProperties.length;
      const totalUnits = allUnits.length;
      const occupancyRate = calculateOccupancyFromUnits(allUnits);
      const occupiedUnits = allUnits.filter(u => u.status === 'Occupied').length;

      // 2. Financial Metrics
      const totalRevenue = calculateRevenue(allReceivables, 'all');
      const collectedRevenue = calculateRevenue(allReceivables, 'collected');
      const totalExpenses = calculateExpenses(allBills, 'all');
      const paidExpenses = calculateExpenses(allBills, 'paid');
      const noi = calculateNOI(allReceivables, allBills, 'all');
      const collectedNoi = calculateNOI(allReceivables, allBills, 'collected');

      // 3. ROI Calculation (annual NOI / total property value)
      const portfolioValue = allProperties.reduce((sum: number, p: Property) =>
        sum + ((p.noi || 0) * 180), // Simple 18 cap rate estimate
        0
      );
      const roi = calculateROI(noi * 12, portfolioValue);

      // 4. Cash Flow Metrics
      const pendingReceivables = calculatePendingReceivables(allReceivables);
      const overdueReceivables = calculateOverdueReceivables(allReceivables);
      const overdueReceivableCount = calculateOverdueReceivableCount(allReceivables);
      const pendingBills = calculatePendingBills(allBills);

      // 5. Maintenance Metrics
      const activeTickets = allTickets.filter((t: MaintenanceTicket) => isJobActive(t)).length;
      const completedTickets = allTickets.filter((t: MaintenanceTicket) => isJobCompleted(t)).length;

      // 6. Compliance Metrics
      const totalInspections = allInspections.length;
      const complianceInspections = allInspections.filter((i: Inspection) =>
        i.type === 'Annual' || i.type === 'Routine'
      ).length;
      const activeHCVPrograms = allHCVPrograms.filter((h: HCVProgram) =>
        h.complianceStatus === 'Compliant' || h.complianceStatus === 'At Risk'
      ).length;

      return {
        totalProperties,
        totalUnits,
        occupiedUnits,
        occupancyRate,
        totalRevenue,
        collectedRevenue,
        totalExpenses,
        paidExpenses,
        noi,
        collectedNoi,
        roi,
        pendingReceivables,
        overdueReceivables,
        overdueReceivableCount,
        pendingBills,
        activeTickets,
        completedTickets,
        totalInspections,
        complianceInspections,
        activeHCVPrograms,
        properties: allProperties,
        receivables: allReceivables,
        bills: allBills,
        tickets: allTickets,
        inspections: allInspections,
        hcvPrograms: allHCVPrograms,
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });
};

